// src/components/lesson/diagrams/SentenceDiagram.tsx
//
// Visualizes a sentence as labeled segments with bracket highlights for
// nonrestrictive clauses, marked punctuation slots, and clause types.
//
// Params:
//   {
//     "segments": [
//       { "text": "Maria,", "kind": "subject" },
//       { "text": "who has been studying biology", "kind": "nonrestrictive" },
//       { "text": ", will lead the team.", "kind": "predicate" }
//     ]
//   }
//
// kind values:
//   - "independent" — green outline (independent clause)
//   - "nonrestrictive" — amber outline + bracket label
//   - "subject", "predicate", "fragment" — neutral
//   - "punctuation" — small chip
//   - "highlight" — cyan emphasis

type Segment = { text: string; kind?: string }
type SentenceDiagramParams = {
  segments?: Segment[]
  /** Optional title above the diagram. */
  title?: string
  label?: string
}

const KIND_STYLE: Record<string, string> = {
  independent: 'border-emerald-500/50 bg-emerald-500/10 text-emerald-900 dark:text-emerald-100',
  nonrestrictive: 'border-amber-500/50 bg-amber-500/10 text-amber-900 dark:text-amber-100',
  highlight: 'border-brand/50 bg-brand/15 text-brand-ink',
  punctuation: 'border-slate-500/40 bg-slate-700/40 text-foreground font-mono',
  subject: 'border-border bg-muted/60 text-foreground',
  predicate: 'border-border bg-muted/60 text-foreground',
  fragment: 'border-red-500/50 bg-red-500/10 text-red-800 dark:text-red-200',
}

const KIND_LABEL: Record<string, string> = {
  independent: 'Independent clause',
  nonrestrictive: 'Nonrestrictive (extra info)',
  highlight: 'Key spot',
  punctuation: 'Punctuation',
  fragment: 'Fragment',
}

export default function SentenceDiagram({ params }: { params: Record<string, unknown> }) {
  const p = params as SentenceDiagramParams
  const segments = Array.isArray(p.segments) ? p.segments : []

  const usedKinds = Array.from(
    new Set(
      segments.map((s) => s.kind).filter((k): k is string => !!k && KIND_LABEL[k] !== undefined),
    ),
  )

  return (
    <figure
      role="img"
      aria-label={p.label ?? 'Sentence diagram'}
      className="my-3 rounded-md border border-border bg-card/60 p-3"
    >
      {p.title && (
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {p.title}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1 text-sm">
        {segments.map((seg, i) => {
          const cls = KIND_STYLE[seg.kind ?? ''] ?? 'border-border bg-muted/40 text-foreground'
          return (
            <span key={i} className={`inline-block rounded-md border px-2 py-1 ${cls}`}>
              {seg.text}
            </span>
          )
        })}
      </div>
      {/* Legend */}
      {usedKinds.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-muted-foreground">
          {usedKinds.map((k) => (
            <span key={k} className="inline-flex items-center gap-1">
              <span className={`inline-block h-2 w-2 rounded-sm border ${KIND_STYLE[k]}`} />
              {KIND_LABEL[k]}
            </span>
          ))}
        </div>
      )}
    </figure>
  )
}
